import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material";

// Admin view of Order.priceNegotiation: every price proposal, who made it,
// what happened to it and when, plus the listed vs agreed price per item.
// Proposals are append-only, so this is the record an admin uses to settle
// "we never agreed to that price". The agreed price, not the listed one, is
// what the buyer should have paid.

const NEGOTIATION_STATUS = {
  PROPOSED: { label: "Offer open", color: "warning" },
  COUNTERED: { label: "Offer open", color: "warning" },
  ACCEPTED: { label: "Price agreed", color: "success" },
  DECLINED: { label: "Not agreed", color: "default" },
  EXPIRED: { label: "Expired", color: "default" },
};

const PROPOSAL_STATUS = {
  PENDING: { label: "Awaiting response", color: "warning" },
  ACCEPTED: { label: "Accepted", color: "success" },
  REJECTED: { label: "Declined", color: "error" },
  COUNTERED: { label: "Countered", color: "info" },
  WITHDRAWN: { label: "Withdrawn", color: "default" },
  EXPIRED: { label: "Expired", color: "default" },
};

const formatCurrency = (cents, currency) => {
  const amount = ((cents || 0) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${currency} ${amount}` : amount;
};

const formatDateTime = (date) =>
  date
    ? new Date(date).toLocaleString(undefined, { timeZoneName: "short" })
    : null;

const productIdOf = (value) => String(value?._id ?? value);

// Delivery fee `proposal` was made against: its own when it proposed one.
// Otherwise the original fee, since the fee only ever changes through an
// accepted proposal, which ends the negotiation.
const proposalDeliveryFeeCents = (order, proposal) =>
  proposal.deliveryFeeCents ??
  order.originalDeliveryFeeCents ??
  order.deliveryFeeCents ??
  0;

// Order total if `proposal` were accepted (mirrors the backend).
const proposalTotalCents = (order, proposal) =>
  (order.items || []).reduce((sum, item) => {
    const proposed = proposal.items?.find(
      (it) => productIdOf(it.product) === productIdOf(item.product),
    );
    return (
      sum +
      (proposed ? proposed.priceCents * item.quantity : item.lineTotalCents)
    );
  }, 0) + proposalDeliveryFeeCents(order, proposal);

// "NGN 20.00 (NGN 15.00 + NGN 5.00 delivery fee)", or just the total when
// there's no delivery fee.
const withDeliverySplit = (totalCents, deliveryFeeCents, currency) =>
  deliveryFeeCents
    ? `${formatCurrency(totalCents, currency)} (${formatCurrency(totalCents - deliveryFeeCents, currency)} + ${formatCurrency(deliveryFeeCents, currency)} delivery fee)`
    : formatCurrency(totalCents, currency);

const listedTotalCents = (order) =>
  (order.items || []).reduce(
    (sum, item) =>
      sum + (item.originalPriceCents ?? item.priceCents) * item.quantity,
    0,
  ) + (order.originalDeliveryFeeCents ?? order.deliveryFeeCents ?? 0);

// A counter answers the proposal before it; a proposal after a declined or
// expired one opens a new negotiation.
const isCounterAt = (proposals, index) =>
  index > 0 && proposals[index - 1]?.status === "COUNTERED";

const OrderPriceNegotiation = ({ order }) => {
  const negotiation = order.priceNegotiation;
  const proposals = negotiation?.proposals || [];
  if (!proposals.length) return null;

  const currency = order.currency;
  const buyerName = order.user?.name || "Buyer";
  const sellerName = order.shop?.shopName || "Seller";
  const actorChip = (role) => (
    <Chip
      size="small"
      variant="outlined"
      color={role === "SELLER" ? "primary" : "secondary"}
      label={
        role === "SELLER" ? `Seller · ${sellerName}` : `Buyer · ${buyerName}`
      }
    />
  );
  // respondedBy is a user id; map it back to the party that responded.
  const responderRole = (proposal) => {
    const id = proposal.respondedBy && String(proposal.respondedBy);
    if (!id) return null;
    if (id === String(order.user?._id ?? order.user)) return "USER";
    if (id === String(order.shop?.createdBy?._id ?? order.shop?.createdBy))
      return "SELLER";
    return null;
  };

  const status = NEGOTIATION_STATUS[negotiation.status] || {
    label: negotiation.status,
  };
  const listed = listedTotalCents(order);
  const priceChanged = listed !== order.totalCents;
  const negotiatedItems = (order.items || []).filter(
    (item) =>
      item.originalPriceCents != null &&
      item.originalPriceCents !== item.priceCents,
  );
  const listedDeliveryFee =
    order.originalDeliveryFeeCents ?? order.deliveryFeeCents ?? 0;
  const deliveryFeeChanged =
    order.originalDeliveryFeeCents != null &&
    order.originalDeliveryFeeCents !== order.deliveryFeeCents;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          gap={1}
          mb={0.5}
        >
          <Typography fontWeight={600}>Price Negotiation</Typography>
          <Chip
            size="small"
            label={status.label}
            color={status.color || "default"}
          />
          {negotiation.openedAtCheckout && (
            <Chip
              size="small"
              variant="outlined"
              label="Offer made at checkout"
            />
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ ml: "auto" }}
          >
            {proposals.length} proposal{proposals.length === 1 ? "" : "s"}
          </Typography>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mb={1.5}
        >
          Every offer and response, newest first (#1 is the first offer).
          Proposals are never edited or removed.
        </Typography>

        <Box mb={1.5}>
          <Typography variant="body2">
            Listed total: {formatCurrency(listed, currency)}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {negotiation.status === "ACCEPTED"
              ? "Agreed total"
              : "Current total"}
            :{" "}
            {withDeliverySplit(
              order.totalCents,
              order.deliveryFeeCents,
              currency,
            )}
            {priceChanged && listed > 0 && (
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                ml={1}
              >
                ({Math.round(((order.totalCents - listed) / listed) * 100)}% vs
                listed)
              </Typography>
            )}
          </Typography>
          {negotiatedItems.map((item, i) => (
            <Typography
              key={i}
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {item.productName}:{" "}
              <s>{formatCurrency(item.originalPriceCents, currency)}</s>{" "}
              {formatCurrency(item.priceCents, currency)} each × {item.quantity}
              {item.pricingMode === "OFFERS" &&
                " (struck price is the hidden asking price)"}
            </Typography>
          ))}
          {deliveryFeeChanged && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {order.deliveryFeeCents > listedDeliveryFee ? (
                <>
                  Delivery fee raised from{" "}
                  {formatCurrency(listedDeliveryFee, currency)} to{" "}
                  {formatCurrency(order.deliveryFeeCents, currency)} (agreed in
                  the negotiation below)
                </>
              ) : (
                <>
                  Delivery fee:{" "}
                  <s>{formatCurrency(listedDeliveryFee, currency)}</s>{" "}
                  {formatCurrency(order.deliveryFeeCents, currency)}
                </>
              )}
            </Typography>
          )}
        </Box>

        {negotiation.status === "ACCEPTED" && order.dispute?.reason && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            A price was agreed in-app. Payment proof should match{" "}
            <strong>{formatCurrency(order.totalCents, currency)}</strong>, not
            the listed {formatCurrency(listed, currency)}. A request to pay a
            different amount outside the app is a red flag.
          </Alert>
        )}

        <Stack divider={<Divider flexItem />} spacing={1.5}>
          {/* Newest first; `index` stays the proposal's position, so #N and
              the counter/offer wording are unchanged. */}
          {proposals
            .map((proposal, index) => ({ proposal, index }))
            .reverse()
            .map(({ proposal, index }) => {
              const proposalStatus = PROPOSAL_STATUS[proposal.status] || {
                label: proposal.status,
              };
              const responder = responderRole(proposal);
              return (
                <Box key={proposal._id || index}>
                  <Box
                    display="flex"
                    flexWrap="wrap"
                    alignItems="center"
                    gap={1}
                    mb={0.5}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      #{index + 1}
                    </Typography>
                    {actorChip(proposal.proposedBy)}
                    <Typography variant="body2">
                      {isCounterAt(proposals, index)
                        ? "countered with"
                        : "offered"}{" "}
                      <strong>
                        {withDeliverySplit(
                          proposalTotalCents(order, proposal),
                          proposalDeliveryFeeCents(order, proposal),
                          currency,
                        )}
                      </strong>
                    </Typography>
                    <Chip
                      size="small"
                      label={proposalStatus.label}
                      color={proposalStatus.color || "default"}
                    />
                    {/* The seller asked for more than the listed delivery fee:
                      worth a look on its own, and the proposal's note holds
                      their reason. */}
                    {proposal.proposedBy === "SELLER" &&
                      proposal.deliveryFeeCents > listedDeliveryFee && (
                        <Chip
                          size="small"
                          color="warning"
                          variant="outlined"
                          label="Delivery fee raised"
                        />
                      )}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: "auto" }}
                    >
                      {formatDateTime(proposal.createdAt)}
                    </Typography>
                  </Box>
                  {proposal.items?.map((proposed, i) => {
                    const item = order.items?.find(
                      (it) =>
                        productIdOf(it.product) ===
                        productIdOf(proposed.product),
                    );
                    return (
                      <Typography
                        key={i}
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {item?.productName || "Unknown item"}:{" "}
                        {formatCurrency(proposed.priceCents, currency)} each
                        {item?.originalPriceCents != null &&
                          (item.pricingMode === "OFFERS"
                            ? ` (hidden asking price ${formatCurrency(item.originalPriceCents, currency)})`
                            : ` (listed ${formatCurrency(item.originalPriceCents, currency)})`)}
                      </Typography>
                    );
                  })}
                  {proposal.deliveryFeeCents != null && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Delivery fee:{" "}
                      {formatCurrency(proposal.deliveryFeeCents, currency)} for
                      the order (listed{" "}
                      {formatCurrency(listedDeliveryFee, currency)})
                    </Typography>
                  )}
                  {proposal.note && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 0.5,
                        fontStyle: "italic",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      "{proposal.note}"
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    mt={0.5}
                  >
                    {proposal.respondedAt
                      ? `${proposalStatus.label} ${formatDateTime(proposal.respondedAt)}${
                          responder
                            ? ` by the ${responder === "SELLER" ? "seller" : "buyer"}`
                            : ""
                        }`
                      : proposal.status === "PENDING"
                        ? `Expires ${formatDateTime(proposal.expiresAt)}`
                        : null}
                  </Typography>
                  {proposal.responseNote && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 0.25,
                        fontStyle: "italic",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {responder === "SELLER"
                        ? "Seller"
                        : responder === "USER"
                          ? "Buyer"
                          : "Response"}
                      : "{proposal.responseNote}"
                    </Typography>
                  )}
                </Box>
              );
            })}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default OrderPriceNegotiation;
